"use client"

import type React from "react"
import { useState, useCallback } from "react"
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner"
import { useChangeTracker } from "../../hooks/useChangeTracker"
import { usePasswordValidation } from "../../hooks/usePasswordValidation"
import { usePasswordApi } from "../../hooks/usePasswordApi"
import { ACCOUNT_CONSTANTS, INITIAL_PASSWORD_DATA } from "../../constants/account-details"
import type { PasswordData, FormState, PasswordVisibility } from "../../types/account-details"
import { useToast } from "../../context/ToastContext";

const ChangePasswordForm: React.FC = () => {
  const { data: passwordData, hasChanges, updateField, resetChanges } = useChangeTracker(INITIAL_PASSWORD_DATA)
  const { validatePasswords } = usePasswordValidation()
  const { isChangingPassword, changePasswordApi } = usePasswordApi()
  const { error, success } = useToast();

  const [formState, setFormState] = useState<FormState>({
    loading: false,
    error: null,
    success: null,
  })

  const [showPasswords, setShowPasswords] = useState<PasswordVisibility>({
    old: false,
    new: false,
    confirm: false,
  })

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target
      updateField(name as keyof PasswordData, value)

      // Clear messages when user starts typing
      if (formState.error || formState.success) {
        setFormState((prev) => ({ ...prev, error: null, success: null }))
      }
    },
    [updateField, formState.error, formState.success],
  )

  const togglePasswordVisibility = useCallback((field: keyof PasswordVisibility) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }))
  }, [])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      // Validate passwords
      const validationError = validatePasswords(passwordData)
      if (validationError) {
        setFormState((prev) => ({ ...prev, error: validationError }))
        return
      }

      setFormState({ loading: false, error: null, success: null })

      try {
        const res = await changePasswordApi(passwordData)

        if (res) {
          success(ACCOUNT_CONSTANTS.SUCCESS_MESSAGES.PASSWORD_CHANGED)
          setFormState({
            loading: false,
            error: null,
            success: ACCOUNT_CONSTANTS.SUCCESS_MESSAGES.PASSWORD_CHANGED,
          })

          // Reset form after successful change
          resetChanges()

          // Clear success message after 3 seconds
          setTimeout(() => {
            setFormState((prev) => ({ ...prev, success: null }))
          }, 3000)
        }
      } catch (err: any) {
        console.error("Error changing password:", err)
        error(err.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.PASSWORD_CHANGE_FAILED)
        setFormState({
          loading: false,
          error: err.message || ACCOUNT_CONSTANTS.ERROR_MESSAGES.PASSWORD_CHANGE_FAILED,
          success: null,
        })
      }
    },
    [validatePasswords, passwordData, changePasswordApi, resetChanges],
  )

  const handleRetryPasswordChange = useCallback(async () => {
    if (!hasChanges) {
      setFormState((prev) => ({ ...prev, error: "Please fill in all password fields to retry." }))
      return
    }

    // Retry the password change
    await handleSubmit({ preventDefault: () => {} } as React.FormEvent)
  }, [hasChanges, handleSubmit])

  const passwordFields = [
    {
      name: "oldPassword",
      label: "Current Password",
      type: "old",
      fullWidth: true,
      placeholder: "Enter your current password",
    },
    {
      name: "newPassword",
      label: "New Password",
      type: "new",
      hint: ACCOUNT_CONSTANTS.MESSAGES.PASSWORD_HINT,
      placeholder: "Enter your new password",
    },
    {
      name: "confirmPassword",
      label: "Confirm New Password",
      type: "confirm",
      placeholder: "Confirm your new password",
    },
  ]

  return (
    <div className="form-section password-section">
      <h3 className="section-title">Change Password</h3>
      <p className="section-description">
        Update your password to keep your account secure. Make sure to use a strong password.
      </p>

      <form onSubmit={handleSubmit}>
        <div className="password-grid">
          {passwordFields.map((field) => (
            <div key={field.name} className={`form-group ${field.fullWidth ? "full-width" : ""}`}>
              <label htmlFor={field.name} className="form-label">
                {field.label}
                <span className="required">*</span>
              </label>
              <div className="password-input-wrapper">
                <input
                  id={field.name}
                  name={field.name}
                  type={showPasswords[field.type as keyof PasswordVisibility] ? "text" : "password"}
                  value={passwordData[field.name as keyof PasswordData]}
                  onChange={handleInputChange}
                  placeholder={field.placeholder}
                  className="form-input password-input"
                  disabled={isChangingPassword}
                  required
                  autoComplete={field.name === "oldPassword" ? "current-password" : "new-password"}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility(field.type as keyof PasswordVisibility)}
                  className="password-toggle"
                  disabled={isChangingPassword}
                  aria-label={`${showPasswords[field.type as keyof PasswordVisibility] ? "Hide" : "Show"} ${field.label}`}
                  title={`${showPasswords[field.type as keyof PasswordVisibility] ? "Hide" : "Show"} password`}
                >
                  {showPasswords[field.type as keyof PasswordVisibility] ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                      <line x1="1" y1="1" x2="23" y2="23" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  )}
                </button>
              </div>
              {field.hint && <small className="form-hint">{field.hint}</small>}
            </div>
          ))}
        </div>

        <div className="password-requirements">
          <h4>Password Requirements:</h4>
          <ul>
            <li className={passwordData.newPassword.length >= 8 ? "requirement-met" : ""}>
              At least 8 characters long
            </li>
            <li className={/[A-Z]/.test(passwordData.newPassword) ? "requirement-met" : ""}>
              Contains uppercase letter
            </li>
            <li className={/[a-z]/.test(passwordData.newPassword) ? "requirement-met" : ""}>
              Contains lowercase letter
            </li>
            <li className={/\d/.test(passwordData.newPassword) ? "requirement-met" : ""}>Contains number</li>
            <li className={/[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword) ? "requirement-met" : ""}>
              Contains special character
            </li>
          </ul>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={!hasChanges || isChangingPassword} className="btn btn-primary">
            {isChangingPassword ? (
              <>
                <LoadingSpinner size="small" />
                Changing Password...
              </>
            ) : (
              "Change Password"
            )}
          </button>

          {hasChanges && !isChangingPassword && (
            <button type="button" onClick={resetChanges} className="btn btn-secondary">
              Cancel
            </button>
          )}
        </div>

        {formState.error && (
          <div className="form-message error">
            <span className="message-icon">⚠️</span>
            <div className="message-content">
              <span>{formState.error}</span>
              {hasChanges && (
                <button onClick={handleRetryPasswordChange} className="account-retry-button">
                  Retry
                </button>
              )}
            </div>
          </div>
        )}

        {formState.success && (
          <div className="form-message success">
            <span className="message-icon">✅</span>
            {formState.success}
          </div>
        )}
      </form>

      <div className="security-tips">
        <h4>Security Tips:</h4>
        <ul>
          <li>Use a unique password that you don't use elsewhere</li>
          <li>Consider using a password manager</li>
          <li>Change your password regularly</li>
          <li>Never share your password with anyone</li>
        </ul>
      </div>
    </div>
  )
}

export default ChangePasswordForm
